/**
 * 7 built-in automation rule templates.
 *
 * Templates are pre-configured rules that users can one-click apply from the
 * Rule Builder UI. Each template specifies which PM component types it applies to.
 */

import type { AutomationRule, AutomationRuleTemplate } from '@/types/pm-types';

export const AUTOMATION_RULE_TEMPLATES: AutomationRuleTemplate[] = [
  {
    id: 'auto-set-status-on-add',
    name: 'Auto-set status on add',
    description: 'New items start with status "Todo"',
    trigger: { type: 'onItemAdd' },
    conditions: [],
    actions: [{ type: 'setField', config: { field: 'status', value: 'Todo' } }],
    applicableTo: ['kanban-board', 'gantt-chart', 'calendar'],
  },
  {
    id: 'auto-complete-date',
    name: 'Auto-set completion date',
    description: 'Set completion date when moved to Done',
    trigger: { type: 'onColumnMove', config: { toColumn: 'done' } },
    conditions: [],
    actions: [{ type: 'setField', config: { field: 'completedDate', value: '@today' } }],
    applicableTo: ['kanban-board'],
  },
  {
    id: 'auto-complete-parent',
    name: 'Auto-complete parent task',
    description: 'Mark parent done when all sub-tasks complete',
    trigger: {
      type: 'onSubTasksComplete',
      config: { field: 'parentId', statusField: 'status', statusValue: 'Done' },
    },
    conditions: [],
    actions: [{ type: 'setField', config: { field: 'status', value: 'Done' } }],
    applicableTo: ['kanban-board', 'gantt-chart'],
  },
  {
    id: 'auto-archive-stale',
    name: 'Auto-move stale items',
    description: 'Move items unchanged for 14 days to Stale column',
    trigger: { type: 'onStale', config: { days: 14, field: 'updatedAt' } },
    conditions: [{ type: 'fieldNotEquals', field: 'status', value: 'Done' }],
    actions: [{ type: 'moveToColumn', config: { column: 'Stale' } }],
    applicableTo: ['kanban-board'],
  },
  {
    id: 'auto-archive-completed',
    name: 'Auto-archive completed items',
    description: 'Archive items in Done for 7+ days',
    trigger: { type: 'onStale', config: { days: 7, field: 'updatedAt' } },
    conditions: [{ type: 'fieldEquals', field: 'status', value: 'Done' }],
    actions: [{ type: 'archive', config: {} }],
    applicableTo: ['kanban-board', 'gantt-chart', 'calendar'],
  },
  {
    id: 'auto-set-priority',
    name: 'Auto-set default priority',
    description: 'Set priority to Medium for new items without one',
    trigger: { type: 'onItemAdd' },
    conditions: [{ type: 'fieldEmpty', field: 'priority' }],
    actions: [{ type: 'setField', config: { field: 'priority', value: 'Medium' } }],
    applicableTo: ['kanban-board', 'gantt-chart', 'calendar'],
  },
  {
    id: 'auto-flag-overdue',
    name: 'Auto-flag overdue items',
    description: 'Add overdue tag when due date passes',
    trigger: { type: 'onDateReached', config: { field: 'dueDate' } },
    conditions: [{ type: 'fieldNotEquals', field: 'status', value: 'Done' }],
    actions: [{ type: 'addTag', config: { tag: 'overdue', field: 'tags' } }],
    applicableTo: ['kanban-board', 'gantt-chart', 'calendar'],
  },
];

let instanceCounter = 0;

/**
 * Creates a new AutomationRule from a template with a unique ID.
 */
export function instantiateTemplate(
  templateId: string,
  overrides?: Partial<AutomationRule>,
): AutomationRule | null {
  const template = AUTOMATION_RULE_TEMPLATES.find((t) => t.id === templateId);
  if (!template) return null;

  instanceCounter++;
  const rule: AutomationRule = {
    id: overrides?.id ?? `${template.id}-${Date.now()}-${instanceCounter}`,
    name: overrides?.name ?? template.name,
    enabled: overrides?.enabled ?? true,
    trigger: overrides?.trigger ?? { ...template.trigger, config: template.trigger.config ? { ...template.trigger.config } : undefined },
    conditions: overrides?.conditions ?? template.conditions.map((c) => ({ ...c })),
    actions: overrides?.actions ?? template.actions.map((a) => ({ ...a, config: { ...a.config } })),
  };

  return rule;
}

/**
 * Returns templates applicable to a given component type.
 */
export function getTemplatesForComponentType(
  componentType: string,
): AutomationRuleTemplate[] {
  return AUTOMATION_RULE_TEMPLATES.filter((t) =>
    t.applicableTo.includes(componentType as 'kanban-board' | 'gantt-chart' | 'calendar'),
  );
}
